import React, { useMemo } from 'react';
import { LinearProgress, Box, Card, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { emphasize } from '@mui/material/styles';

const getProgressColor = (percent) => {
  if (percent < 50) return '#f44336';
  if (percent < 80) return '#F9AA33';
  return '#4caf50';
};

const CurrentCropsTable = ({ crops = [], harvests = [] }) => {
  // Filter crops as in ZoneSelectList
  const filteredCrops = useMemo(() =>
    crops.filter(crop => !crop.deleted && !crop.terminated),
    [crops]
  );

  // Prepare rows for DataGridPro
  const rows = useMemo(() => {
    return filteredCrops.map((crop, idx) => {
      const cropHarvests = harvests.filter(h => h.crop_id === crop.id);
      const totalYield = cropHarvests.reduce((sum, h) => sum + (h.harvest_amt || 0), 0);
      const lastHarvestDate = cropHarvests.reduce((latest, h) => {
        const date = new Date(h.date);
        return date > latest ? date : latest;
      }, new Date(crop.planting_date));
      
      // Use the new direct fields instead of nested objects
      const plotArea = crop.plot_area || 0;
      const actualYield = plotArea > 0 ? (totalYield / plotArea).toFixed(2) : 0;
      const targetYield = crop.target_yield || 0;
      const percent = targetYield > 0 ? Math.min((actualYield / targetYield) * 100, 100) : 0;
      
      return {
        id: crop.id || idx,
        variety: crop.variety_name_cl || crop.variety_name || 'Unknown',
        plantType: crop.plant_type_cl || crop.plant_type || 'Unknown',
        section: crop.zone_name || 'N/A',
        plot: crop.plot_name || 'N/A',
        plantingDate: crop.planting_date || '',
        transplantDate: crop.transplant_date || '',
        lastHarvestDate: cropHarvests.length ? lastHarvestDate.toISOString().split('T')[0] : '',
        progress: percent,
        actual: actualYield,
        target: targetYield,
      };
    });
  }, [filteredCrops, harvests]);

  // Sort by actual yield, highest first
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => parseFloat(b.actual) - parseFloat(a.actual));
  }, [rows]);

  // DataGridPro columns
  const columns = [
    { field: 'plantType', headerName: 'Plant Type', flex: 1, minWidth: 120 },
    { field: 'section', headerName: 'Section', flex: 1, minWidth: 120 },
    { field: 'plot', headerName: 'Plot', flex: 1, minWidth: 120 },
    {
      field: 'progress',
      headerName: 'Progress',
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ 
          width: '100%', 
          height: '100%',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          px: 1
        }}>
          <Box sx={{ width: '80%', maxWidth: 100 }}>
            <LinearProgress
              variant="determinate"
              value={params.value}
              sx={{ 
                height: 8, 
                borderRadius: 4, 
                background: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': { 
                  backgroundColor: getProgressColor(params.value) 
                } 
              }}
            />
          </Box>
        </Box>
      ),
    },
    { field: 'actual', headerName: 'Actual', flex: 1, minWidth: 80, type: 'number' },
    { field: 'target', headerName: 'Target', flex: 1, minWidth: 80, type: 'number' },
    { field: 'variety', headerName: 'Variety', flex: 1, minWidth: 120 },
    { field: 'plantingDate', headerName: 'Planting Date', flex: 1, minWidth: 120 },
    { field: 'transplantDate', headerName: 'Transplant Date', flex: 1, minWidth: 120 },
    { field: 'lastHarvestDate', headerName: 'Last Harvest', flex: 1, minWidth: 120 },
  ];

  return (
    <Card sx={{ 
      backgroundColor: '#181F2A', 
      borderRadius: 1.5, 
      boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)', 
      p: 0,
      height: '100%',
      maxHeight: { xs: 630, md: 630 },
      overflow: 'auto',
      mt: 1,
      mr: 2,
      mb: { xs: 8, md: 0 }
    }}>
      <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 500, fontSize: 15, pt: 2, px: 3, pb: 0.5 }}>
        Current Crops
      </Typography>
      <Box sx={{ height: 'calc(100% - 40px)', width: '100%', bgcolor: 'transparent', px: 3 }}>
        <DataGridPro
          rows={sortedRows}
          columns={columns}
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          pagination
          sx={{
            height: '100%',
            bgcolor: 'transparent',
            color: '#fff',
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: '#222C2F !important',
              color: '#fff',
              fontWeight: 500,
              fontSize: 13,
              borderBottom: '1px solid rgba(255,255,255,0.12)',
            },
            '& .MuiDataGrid-columnHeader': {
              bgcolor: '#222C2F !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: '#fff',
              fontWeight: 500,
              fontSize: 13,
            },
            '& .MuiDataGrid-cell': {
              bgcolor: 'transparent',
              color: '#fff',
              fontSize: 12,
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              py: 1,
            },
            '& .MuiDataGrid-virtualScroller': {
              bgcolor: 'transparent',
            },
            '& .MuiDataGrid-row': {
              height: 48,
            },
            '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
              color: '#F9AA33',
            },
          }}
          getRowId={row => row.id}
        />
      </Box>
    </Card>
  );
};

export default CurrentCropsTable; 